import React from 'react';
import type { WorkflowSourceItem } from '@/types/workflow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SourceSelectionStepProps {
  selectedSourceIds: string[];
  currentPageId: string;
  availableSources: WorkflowSourceItem[];
  sourcesLoading: boolean;
  onAddSource: (sourceId: string) => void;
  onRemoveSource: (sourceId: string) => void;
}

const PageChipIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1" y="1" width="10" height="10" rx="1.2" />
    <path d="M1 4.5h10" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M2 2l6 6M8 2l-6 6" />
  </svg>
);

function getSourceLabel(source: WorkflowSourceItem): string {
  return source.name || 'Untitled Page';
}

const SourceChip: React.FC<{
  source: WorkflowSourceItem;
  isLocked: boolean;
  onRemove: () => void;
}> = ({ source, isLocked, onRemove }) => (
  <div
    className="flex items-center gap-2.5 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 dark:border-neutral-700 dark:bg-neutral-800"
    data-testid={`source-chip-${source._meta.id}`}
  >
    <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-300">
      <PageChipIcon />
    </span>
    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-neutral-800 dark:text-neutral-100">
      {getSourceLabel(source)}
    </span>
    {isLocked ? (
      <span
        className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-violet-600 uppercase dark:bg-violet-500/20 dark:text-violet-300"
        data-testid="source-locked-icon"
      >
        Current
      </span>
    ) : (
      <button
        onClick={onRemove}
        className="shrink-0 rounded p-[3px] text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
        aria-label={`Remove ${getSourceLabel(source)}`}
        data-testid={`remove-source-${source._meta.id}`}
      >
        <XIcon />
      </button>
    )}
  </div>
);

export const SourceSelectionStep: React.FC<SourceSelectionStepProps> = ({
  selectedSourceIds,
  currentPageId,
  availableSources,
  sourcesLoading,
  onAddSource,
  onRemoveSource,
}) => {
  const currentSource = availableSources.find((s) => s._meta.id === currentPageId);
  const otherSources = selectedSourceIds
    .filter((id) => id !== currentPageId)
    .map((id) => availableSources.find((s) => s._meta.id === id))
    .filter((s): s is WorkflowSourceItem => s !== undefined);
  const selectedSources = currentSource ? [currentSource, ...otherSources] : otherSources;
  const addableSources = availableSources.filter((s) => !selectedSourceIds.includes(s._meta.id));

  return (
    <div className="flex w-full flex-col gap-4">
      <div>
        <h2 className="mb-1 text-[15px] font-bold text-neutral-900 dark:text-neutral-100">
          Choose sources
        </h2>
        <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
          Select which pages trigger this automation when a lead submits the form.
        </p>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold tracking-[0.06em] text-neutral-400 uppercase dark:text-neutral-500">
          Source pages
        </p>

        <div className="flex flex-col gap-2">
          {selectedSources.map((source) => (
            <SourceChip
              key={source._meta.id}
              source={source}
              isLocked={source._meta.id === currentPageId}
              onRemove={() => onRemoveSource(source._meta.id)}
            />
          ))}
        </div>
      </div>

      {sourcesLoading && (
        <div className="flex justify-center py-2">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
        </div>
      )}

      {!sourcesLoading && addableSources.length > 0 && (
        // key resets the Select to its placeholder after each selection
        <Select
          key={`add-source-${selectedSourceIds.length}`}
          onValueChange={(id) => {
            if (id) onAddSource(id);
          }}
        >
          <SelectTrigger
            className="border-dashed border-neutral-300 !bg-transparent !text-violet-600 hover:!bg-violet-50 data-[state=open]:!outline-none dark:border-neutral-600 dark:!text-violet-400 dark:hover:!bg-violet-900/20"
            hideIcon
          >
            <SelectValue placeholder="+ Add another page" />
          </SelectTrigger>
          <SelectContent>
            {addableSources.map((source) => (
              <SelectItem key={source._meta.id} value={source._meta.id}>
                {getSourceLabel(source)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};
